import React, { useState, useEffect } from 'react';
import { 
  Layers, Shield, AlertTriangle, CheckCircle, Clock, 
  Search, RefreshCw, ChevronDown, ChevronRight, X,
  BarChart3, Target, FileText, ArrowRight, GitCompare
} from 'lucide-react';
import { controlsAPI } from '../lib/api';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, RadialBarChart, RadialBar, Legend } from 'recharts';

const STATUS_OPTIONS = [
  { value: 'not_assessed', label: 'Not Assessed', color: 'bg-slate-500' },
  { value: 'implemented', label: 'Implemented', color: 'bg-green-500' },
  { value: 'partially_implemented', label: 'Partial', color: 'bg-yellow-500' },
  { value: 'not_implemented', label: 'Not Implemented', color: 'bg-red-500' },
  { value: 'not_applicable', label: 'N/A', color: 'bg-slate-400' }
];

const FRAMEWORK_COLORS = {
  cmmc: '#3b82f6',
  nist_csf: '#22c55e',
  hipaa: '#f59e0b',
  iso27001: '#8b5cf6',
  nist_800_53: '#ef4444'
};

const ControlMappingDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [frameworks, setFrameworks] = useState({});
  const [mappings, setMappings] = useState({});
  const [controlStatuses, setControlStatuses] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedFramework, setSelectedFramework] = useState('cmmc');
  const [expandedFamilies, setExpandedFamilies] = useState({});
  
  // Gap Analysis
  const [showGapAnalysis, setShowGapAnalysis] = useState(false);
  const [gapSource, setGapSource] = useState('cmmc');
  const [gapTarget, setGapTarget] = useState('nist_csf');
  const [gapResults, setGapResults] = useState(null);
  const [gapLoading, setGapLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [frameworksRes, mappingsRes, statusesRes, dashboardRes] = await Promise.all([
        controlsAPI.getFrameworks(),
        controlsAPI.getMappings(),
        controlsAPI.getStatuses(),
        controlsAPI.getDashboard()
      ]);
      setFrameworks(frameworksRes.data);
      setMappings(mappingsRes.data);
      setControlStatuses(statusesRes.data);
      setDashboard(dashboardRes.data);
    } catch (error) {
      console.error('Error loading control data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (framework, controlId, status) => {
    try {
      await controlsAPI.updateStatus({
        framework,
        controlId,
        status
      });
      loadData();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const runGapAnalysis = async () => {
    if (gapSource === gapTarget) {
      alert('Source and target frameworks must be different');
      return;
    }
    setGapLoading(true);
    try {
      const res = await controlsAPI.gapAnalysis({
        sourceFramework: gapSource,
        targetFramework: gapTarget
      });
      setGapResults(res.data);
    } catch (error) {
      console.error('Error running gap analysis:', error);
    } finally {
      setGapLoading(false);
    }
  };

  const toggleFamily = (family) => {
    setExpandedFamilies({
      ...expandedFamilies,
      [family]: !expandedFamilies[family]
    });
  };

  const getControlStatus = (framework, controlId) => {
    const status = controlStatuses.find(
      s => s.framework === framework && s.controlId === controlId
    );
    return status?.status || 'not_assessed';
  };

  const getStatusBadgeClass = (status) => {
    const classes = {
      not_assessed: 'bg-slate-500/20 text-slate-400',
      implemented: 'bg-green-500/20 text-green-400',
      partially_implemented: 'bg-yellow-500/20 text-yellow-400',
      not_implemented: 'bg-red-500/20 text-red-400',
      not_applicable: 'bg-slate-400/20 text-slate-500'
    };
    return classes[status] || classes.not_assessed;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-cyan-500 animate-spin" />
      </div>
    );
  }

  const frameworkComplianceData = dashboard?.frameworkCompliance ? 
    Object.entries(dashboard.frameworkCompliance).map(([key, value]) => ({
      name: value.name,
      compliance: value.complianceRate,
      fill: FRAMEWORK_COLORS[key]
    })) : [];

  const familyStatusData = dashboard?.familyStatus ?
    Object.entries(dashboard.familyStatus).map(([key, value]) => ({
      name: key,
      implemented: value.implemented,
      notImplemented: value.notImplemented
    })) : [];

  return (
    <div data-testid="control-mapping-dashboard" className="min-h-screen bg-slate-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg flex items-center justify-center">
                <Layers className="w-6 h-6 text-white" />
              </div>
              Control Framework Mapping
            </h1>
            <p className="text-slate-400 mt-2">Map and track compliance across multiple security frameworks</p>
          </div>
          <button
            onClick={() => setShowGapAnalysis(true)}
            className="flex items-center gap-2 px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg transition-colors"
            data-testid="gap-analysis-btn"
          >
            <GitCompare className="w-4 h-4" />
            Gap Analysis
          </button>
        </div>

        {/* Stats Cards */}
        {dashboard && (
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
            <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-cyan-500/20 rounded-lg flex items-center justify-center">
                  <Layers className="w-5 h-5 text-cyan-400" />
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Frameworks</p>
                  <p className="text-2xl font-bold text-white">{Object.keys(frameworks).length}</p>
                </div>
              </div>
            </div>
            <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                  <Target className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Total Assessed</p>
                  <p className="text-2xl font-bold text-white">{dashboard.overallStats.totalAssessed}</p>
                </div>
              </div>
            </div>
            <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Implemented</p>
                  <p className="text-2xl font-bold text-white">{dashboard.overallStats.implemented}</p>
                </div>
              </div>
            </div>
            <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-yellow-500/20 rounded-lg flex items-center justify-center">
                  <Clock className="w-5 h-5 text-yellow-400" />
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Partial</p>
                  <p className="text-2xl font-bold text-white">{dashboard.overallStats.partiallyImplemented}</p>
                </div>
              </div>
            </div>
            <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-500/20 rounded-lg flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-red-400" />
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Not Implemented</p>
                  <p className="text-2xl font-bold text-white">{dashboard.overallStats.notImplemented}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-slate-700 pb-2">
          {['overview', 'controls', 'mappings'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-t-lg font-medium capitalize transition-colors ${
                activeTab === tab
                  ? 'bg-cyan-600 text-white'
                  : 'text-slate-400 hover:text-white hover:bg-slate-800'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Framework Compliance */}
            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <h3 className="text-lg font-semibold text-white mb-4">Framework Compliance</h3>
              <div className="space-y-4">
                {dashboard?.frameworkCompliance && Object.entries(dashboard.frameworkCompliance).map(([key, value]) => (
                  <div key={key} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-white font-medium">{value.name}</span>
                      <span className="text-slate-400 text-sm">{value.complianceRate}%</span>
                    </div>
                    <div className="h-3 bg-slate-700 rounded-full overflow-hidden">
                      <div 
                        className="h-full rounded-full transition-all"
                        style={{ 
                          width: `${value.complianceRate}%`,
                          backgroundColor: FRAMEWORK_COLORS[key]
                        }}
                      />
                    </div>
                    <div className="flex items-center gap-4 text-xs text-slate-500">
                      <span>{value.implemented} implemented</span>
                      <span>{value.partiallyImplemented} partial</span>
                      <span>{value.totalControls - value.assessed} not assessed</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Control Families */}
            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <h3 className="text-lg font-semibold text-white mb-4">Control Families</h3>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={familyStatusData} layout="vertical">
                    <XAxis type="number" stroke="#64748b" />
                    <YAxis dataKey="name" type="category" stroke="#64748b" width={40} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155' }}
                      labelStyle={{ color: '#f8fafc' }}
                    />
                    <Bar dataKey="implemented" stackId="a" fill="#22c55e" name="Implemented" />
                    <Bar dataKey="notImplemented" stackId="a" fill="#ef4444" name="Not Implemented" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Framework Overview Cards */}
            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 lg:col-span-2">
              <h3 className="text-lg font-semibold text-white mb-4">Supported Frameworks</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                {Object.entries(frameworks).map(([key, fw]) => (
                  <div 
                    key={key}
                    className="p-4 bg-slate-700/50 rounded-lg border border-slate-600 hover:border-cyan-500/50 transition-colors cursor-pointer"
                    onClick={() => {
                      setSelectedFramework(key);
                      setActiveTab('controls');
                    }}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <div 
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: FRAMEWORK_COLORS[key] }}
                      />
                      <span className="text-white font-medium">{fw.name}</span>
                    </div>
                    <p className="text-slate-400 text-xs mb-2">{fw.fullName}</p>
                    <p className="text-slate-500 text-xs">Version: {fw.version}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Controls Tab */}
        {activeTab === 'controls' && (
          <div>
            {/* Framework Selector */}
            <div className="mb-6 flex items-center gap-4">
              <span className="text-slate-400">Select Framework:</span>
              <div className="flex gap-2">
                {Object.entries(frameworks).map(([key, fw]) => (
                  <button
                    key={key}
                    onClick={() => setSelectedFramework(key)}
                    className={`px-4 py-2 rounded-lg transition-colors ${
                      selectedFramework === key
                        ? 'text-white'
                        : 'bg-slate-800 text-slate-400 hover:text-white'
                    }`}
                    style={{
                      backgroundColor: selectedFramework === key ? FRAMEWORK_COLORS[key] : undefined
                    }}
                  >
                    {fw.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Control Families */}
            <div className="space-y-4">
              {Object.entries(mappings).map(([familyCode, familyData]) => {
                const controls = familyData[selectedFramework] || [];
                if (controls.length === 0) return null;
                
                return (
                  <div key={familyCode} className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
                    <button
                      onClick={() => toggleFamily(familyCode)}
                      className="w-full p-4 flex items-center justify-between hover:bg-slate-700/50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-cyan-400 font-mono">{familyCode}</span>
                        <span className="text-white font-medium">{familyData.name}</span>
                        <span className="text-slate-500 text-sm">({controls.length} controls)</span>
                      </div>
                      {expandedFamilies[familyCode] ? (
                        <ChevronDown className="w-5 h-5 text-slate-400" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-slate-400" />
                      )}
                    </button>
                    
                    {expandedFamilies[familyCode] && (
                      <div className="border-t border-slate-700">
                        {controls.map((controlId) => {
                          const status = getControlStatus(selectedFramework, controlId);
                          return (
                            <div 
                              key={controlId}
                              className="p-4 border-b border-slate-700/50 last:border-b-0 flex items-center justify-between hover:bg-slate-700/30"
                            >
                              <div className="flex items-center gap-4">
                                <span className="text-white font-mono">{controlId}</span>
                              </div>
                              <select
                                value={status}
                                onChange={(e) => handleUpdateStatus(selectedFramework, controlId, e.target.value)}
                                className={`px-3 py-1 rounded-lg text-sm border-0 focus:ring-2 focus:ring-cyan-500 ${getStatusBadgeClass(status)}`}
                                style={{ backgroundColor: 'transparent' }}
                              >
                                {STATUS_OPTIONS.map((opt) => (
                                  <option key={opt.value} value={opt.value} className="bg-slate-800 text-white">
                                    {opt.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Mappings Tab */}
        {activeTab === 'mappings' && (
          <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
            <div className="p-4 border-b border-slate-700">
              <h3 className="text-lg font-semibold text-white">Cross-Framework Control Mappings</h3>
              <p className="text-slate-400 text-sm">See how controls map across different frameworks</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-700/50">
                    <th className="text-left p-4 text-slate-400 font-medium">Family</th>
                    {Object.entries(frameworks).map(([key, fw]) => (
                      <th key={key} className="text-left p-4 text-slate-400 font-medium">
                        <span style={{ color: FRAMEWORK_COLORS[key] }}>{fw.name}</span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(mappings).map(([familyCode, familyData]) => (
                    <tr key={familyCode} className="border-t border-slate-700">
                      <td className="p-4">
                        <div>
                          <span className="text-cyan-400 font-mono">{familyCode}</span>
                          <p className="text-slate-400 text-sm">{familyData.name}</p>
                        </div>
                      </td>
                      {Object.keys(frameworks).map((fwKey) => {
                        const controls = familyData[fwKey] || [];
                        return (
                          <td key={fwKey} className="p-4">
                            {controls.length > 0 ? (
                              <div className="flex flex-wrap gap-1">
                                {controls.slice(0, 3).map((c) => (
                                  <span 
                                    key={c}
                                    className="px-2 py-0.5 bg-slate-700 text-slate-300 rounded text-xs font-mono"
                                  >
                                    {c}
                                  </span>
                                ))}
                                {controls.length > 3 && (
                                  <span className="text-slate-500 text-xs">+{controls.length - 3}</span>
                                )}
                              </div>
                            ) : (
                              <span className="text-slate-600 text-sm">-</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Gap Analysis Modal */}
      {showGapAnalysis && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-xl w-full max-w-4xl border border-slate-700 max-h-[90vh] flex flex-col">
            <div className="p-6 border-b border-slate-700 flex items-center justify-between flex-shrink-0">
              <div>
                <h3 className="text-xl font-semibold text-white">Gap Analysis</h3>
                <p className="text-slate-400 text-sm">Compare compliance across frameworks</p>
              </div>
              <button onClick={() => {
                setShowGapAnalysis(false);
                setGapResults(null);
              }} className="text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Framework Selection */}
            <div className="p-6 border-b border-slate-700 flex-shrink-0">
              <div className="flex items-center gap-4">
                <div>
                  <label className="block text-slate-300 text-sm mb-1">Source Framework</label>
                  <select
                    value={gapSource}
                    onChange={(e) => setGapSource(e.target.value)}
                    className="px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  >
                    {Object.entries(frameworks).map(([key, fw]) => (
                      <option key={key} value={key}>{fw.name}</option>
                    ))}
                  </select>
                </div>
                <ArrowRight className="w-6 h-6 text-slate-500 mt-6" />
                <div>
                  <label className="block text-slate-300 text-sm mb-1">Target Framework</label>
                  <select
                    value={gapTarget}
                    onChange={(e) => setGapTarget(e.target.value)}
                    className="px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  >
                    {Object.entries(frameworks).map(([key, fw]) => (
                      <option key={key} value={key}>{fw.name}</option>
                    ))}
                  </select>
                </div>
                <button
                  onClick={runGapAnalysis}
                  disabled={gapLoading}
                  className="mt-6 px-6 py-2 bg-cyan-600 hover:bg-cyan-700 disabled:bg-slate-600 text-white rounded-lg flex items-center gap-2"
                >
                  {gapLoading ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <GitCompare className="w-4 h-4" />
                  )}
                  Analyze
                </button>
              </div>
            </div>

            {/* Results */}
            <div className="flex-1 overflow-y-auto p-6">
              {gapResults ? (
                <div className="space-y-6">
                  {/* Summary */}
                  <div className="grid grid-cols-4 gap-4">
                    <div className="p-4 bg-slate-700/50 rounded-lg text-center">
                      <p className="text-2xl font-bold text-white">{gapResults.summary.totalControls}</p>
                      <p className="text-slate-400 text-sm">Total Controls</p>
                    </div>
                    <div className="p-4 bg-green-500/10 rounded-lg text-center border border-green-500/30">
                      <p className="text-2xl font-bold text-green-400">{gapResults.summary.fullyImplemented}</p>
                      <p className="text-slate-400 text-sm">Implemented</p>
                    </div>
                    <div className="p-4 bg-yellow-500/10 rounded-lg text-center border border-yellow-500/30">
                      <p className="text-2xl font-bold text-yellow-400">{gapResults.summary.partiallyImplemented}</p>
                      <p className="text-slate-400 text-sm">Partial</p>
                    </div>
                    <div className="p-4 bg-red-500/10 rounded-lg text-center border border-red-500/30">
                      <p className="text-2xl font-bold text-red-400">{gapResults.summary.gaps}</p>
                      <p className="text-slate-400 text-sm">Gaps</p>
                    </div>
                  </div>

                  {/* Compliance Score */}
                  <div className="p-4 bg-slate-700/50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-white font-medium">Overall Compliance</span>
                      <span className="text-cyan-400 font-bold">{gapResults.summary.compliancePercentage}%</span>
                    </div>
                    <div className="h-3 bg-slate-600 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full transition-all"
                        style={{ width: `${gapResults.summary.compliancePercentage}%` }}
                      />
                    </div>
                  </div>

                  {/* Gaps List */}
                  {gapResults.gaps.length > 0 && (
                    <div>
                      <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-red-400" />
                        Identified Gaps ({gapResults.gaps.length})
                      </h4>
                      <div className="space-y-2 max-h-60 overflow-y-auto">
                        {gapResults.gaps.map((gap, idx) => (
                          <div key={idx} className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                            <div className="flex items-center justify-between">
                              <div>
                                <span className="text-cyan-400 font-mono mr-2">{gap.controlFamily}</span>
                                <span className="text-white">{gap.familyName}</span>
                              </div>
                              <span className="text-red-400 font-mono text-sm">{gap.sourceControl}</span>
                            </div>
                            <p className="text-slate-500 text-xs mt-1">
                              Maps to: {gap.targetControls.slice(0, 3).join(', ')}
                              {gap.targetControls.length > 3 && ` +${gap.targetControls.length - 3} more`}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Partial List */}
                  {gapResults.partiallyImplemented.length > 0 && (
                    <div>
                      <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                        <Clock className="w-4 h-4 text-yellow-400" />
                        Partially Implemented ({gapResults.partiallyImplemented.length})
                      </h4>
                      <div className="space-y-2 max-h-40 overflow-y-auto">
                        {gapResults.partiallyImplemented.map((item, idx) => (
                          <div key={idx} className="p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                            <div className="flex items-center justify-between">
                              <div>
                                <span className="text-cyan-400 font-mono mr-2">{item.controlFamily}</span>
                                <span className="text-white">{item.familyName}</span>
                              </div>
                              <span className="text-yellow-400 font-mono text-sm">{item.sourceControl}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12">
                  <GitCompare className="w-12 h-12 mx-auto text-slate-600 mb-3" />
                  <p className="text-slate-400">Select frameworks and click Analyze to run gap analysis</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ControlMappingDashboard;
